import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import supabase from '../lib/supabaseClient.js';
import * as api from '../lib/api.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentAccount, setCurrentAccount] = useState(null);
  const [currentRole, setCurrentRole] = useState(null);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [needsFirstRunOnboarding, setNeedsFirstRunOnboarding] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [loading, setLoading] = useState(true);
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  const bootstrappingRef = useRef(false);

  const clearContext = useCallback(() => {
    setSession(null);
    setCurrentUser(null);
    setCurrentAccount(null);
    setCurrentRole(null);
    setNeedsOnboarding(false);
    setNeedsFirstRunOnboarding(false);
    setBootstrapping(false);
    bootstrappingRef.current = false;
    setPasswordRecovery(false);
  }, []);

  const clearNeedsFirstRunOnboarding = useCallback(() => {
    setNeedsFirstRunOnboarding(false);
  }, []);

  const bootstrapAccount = useCallback(async (userId, profile) => {
    if (bootstrappingRef.current) {
      return;
    }

    bootstrappingRef.current = true;
    setBootstrapping(true);

    try {
      const { data: plans, error: plansError } = await api.listPlans();

      if (plansError) {
        throw plansError;
      }

      const soloPlan = plans.find((plan) => plan.id === 'solo');

      if (!soloPlan) {
        throw new Error('Could not find the Solo plan.');
      }

      const displayName = profile.name?.trim() || 'My';
      const workspaceName = `${displayName}'s Workspace`;
      const { data: account, error: accountError } = await api.createAccount(
        workspaceName,
        userId,
        soloPlan.id
      );

      if (accountError) {
        throw accountError;
      }

      const { error: memberError } = await api.createAccountMember(
        account.id,
        userId,
        'owner',
        'active'
      );

      if (memberError) {
        throw memberError;
      }

      const membershipResult = await api.getUserAccountMembership(userId);

      if (membershipResult.error) {
        throw membershipResult.error;
      }

      if (!membershipResult.data) {
        throw new Error('Account bootstrap completed but membership was not found.');
      }

      setCurrentAccount(membershipResult.data.accounts);
      setCurrentRole(membershipResult.data.role);
      setNeedsOnboarding(false);
      setNeedsFirstRunOnboarding(true);
    } finally {
      bootstrappingRef.current = false;
      setBootstrapping(false);
    }
  }, []);

  const loadUserContext = useCallback(async (userId) => {
    const [profileResult, membershipResult] = await Promise.all([
      api.getProfile(userId),
      api.getUserAccountMembership(userId),
    ]);

    if (profileResult.error) {
      throw profileResult.error;
    }

    setCurrentUser(profileResult.data);

    if (membershipResult.error) {
      throw membershipResult.error;
    }

    if (!membershipResult.data) {
      await bootstrapAccount(userId, profileResult.data);
      return;
    }

    setCurrentAccount(membershipResult.data.accounts);
    setCurrentRole(membershipResult.data.role);
    setNeedsOnboarding(false);
    setNeedsFirstRunOnboarding(false);
  }, [bootstrapAccount]);

  const refreshContext = useCallback(async () => {
    const { data, error } = await api.getCurrentUser();
    if (error) {
      throw error;
    }

    if (!data.user) {
      clearContext();
      return;
    }

    setSession((current) => current ?? { user: data.user });
    await loadUserContext(data.user.id);
  }, [clearContext, loadUserContext]);

  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      const { data: { session: initialSession } } = await supabase.auth.getSession();

      if (!mounted) {
        return;
      }

      setSession(initialSession);

      if (initialSession?.user) {
        try {
          await loadUserContext(initialSession.user.id);
        } catch (error) {
          console.error('Failed to load auth context', error);
        }
      }

      if (mounted) {
        setLoading(false);
      }
    };

    initialize();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, nextSession) => {
        if (!mounted) {
          return;
        }

        setSession(nextSession);

        if (event === 'PASSWORD_RECOVERY') {
          setPasswordRecovery(true);
        }

        if (nextSession?.user) {
          try {
            await loadUserContext(nextSession.user.id);
          } catch (error) {
            console.error('Failed to refresh auth context', error);
          }
        } else {
          clearContext();
        }

        setLoading(false);
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [clearContext, loadUserContext]);

  const value = useMemo(
    () => ({
      session,
      currentUser,
      currentAccount,
      currentRole,
      needsOnboarding,
      needsFirstRunOnboarding,
      clearNeedsFirstRunOnboarding,
      bootstrapping,
      loading: loading || bootstrapping,
      passwordRecovery,
      setPasswordRecovery,
      refreshContext,
      clearContext,
      isAuthenticated: Boolean(session),
    }),
    [
      session,
      currentUser,
      currentAccount,
      currentRole,
      needsOnboarding,
      needsFirstRunOnboarding,
      clearNeedsFirstRunOnboarding,
      bootstrapping,
      loading,
      passwordRecovery,
      refreshContext,
      clearContext,
    ]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
}
